// src/pages/ChartOfAccounts.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAllAccounts, addAccount, updateAccount, deleteAccount, ACCOUNT_NATURES } from "../firebase/database";

export default function ChartOfAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({ name: "", nature: "Expense" });
  const [saving, setSaving] = useState(false);
  const [filterNature, setFilterNature] = useState("");

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const data = await getAllAccounts();
      setAccounts(data);
    } catch (err) {
      console.error("Error loading accounts:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ name: "", nature: "Expense" });
    setEditingAccount(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setSaving(true);
    try {
      if (editingAccount) {
        await updateAccount(editingAccount.id, formData);
      } else {
        await addAccount(formData);
      }
      resetForm();
      loadAccounts();
    } catch (err) {
      console.error("Error saving account:", err);
      alert("Failed to save account");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (account) => {
    setFormData({
      name: account.name || "",
      nature: account.nature || "Expense",
    });
    setEditingAccount(account);
    setShowForm(true);
  };

  const handleDelete = async (accountId, accountName) => {
    if (!window.confirm(`Are you sure you want to delete "${accountName}"?`)) return;
    try {
      await deleteAccount(accountId);
      setAccounts(accounts.filter(a => a.id !== accountId));
    } catch (err) {
      console.error("Error deleting account:", err);
      alert("Failed to delete account");
    }
  };

  const filteredAccounts = filterNature 
    ? accounts.filter(a => a.nature === filterNature)
    : accounts;

  // Group accounts by nature
  const groupedAccounts = {};
  ACCOUNT_NATURES.forEach(n => { groupedAccounts[n] = []; });
  filteredAccounts.forEach(a => {
    if (groupedAccounts[a.nature]) {
      groupedAccounts[a.nature].push(a);
    }
  });

  const natureColors = {
    "Current Liability": "#DC2626",
    "Long Term Liability": "#EA580C",
    "Fixed Assets": "#7C3AED",
    "Current Assets": "#2563EB",
    "Cash": "#059669",
    "Bank": "#0891B2",
    "Creditor": "#EAB308",
    "Debitor": "#F59E0B",
    "Capital": "#EC4899",
    "Income": "#10B981",
    "Expense": "#EF4444",
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
        <div style={{ color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px" }}>
      <style>{`
        .account-header { display: flex; justify-content: space-between; align-items: center; marginBottom: 24px; gap: 16px; }
        .account-table-container { background: white; border-radius: 12px; boxShadow: 0 1px 3px rgba(0,0,0,0.1); overflow: hidden; }
        .account-mobile-cards { display: none; flex-direction: column; gap: 8px; padding: 12px; }
        .account-card { background: white; border: 1px solid #E5E7EB; border-radius: 8px; padding: 12px; display: flex; align-items: center; justify-content: space-between; }
        .account-actions { display: flex; gap: 8px; }

        @media (max-width: 768px) {
          .account-header { flex-direction: column; align-items: flex-start; }
          .account-header > button { width: 100%; }
          .account-table-container table { display: none; }
          .account-mobile-cards { display: flex; }
        }
      `}</style>

      {/* Header */}
      <div className="account-header" style={{ marginBottom: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            <Link 
              to="/admin" 
              style={{ color: "#6B7280", textDecoration: "none", display: "flex", alignItems: "center" }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1F2937", margin: 0, fontFamily: "DM Sans, sans-serif" }}>
              Chart of Accounts
            </h1>
          </div>
          <p style={{ color: "#6B7280", margin: "4px 0 0 44px", fontFamily: "DM Sans, sans-serif" }}>
            Manage accounts for expenses and income tracking
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          style={{
            padding: "12px 24px",
            background: "#3B82F6",
            color: "white",
            border: "none",
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: "DM Sans, sans-serif",
            cursor: "pointer",
          }}
        >
          + Add Account
        </button>
      </div>

      {/* Filter */}
      <div style={{ marginBottom: 20, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          onClick={() => setFilterNature("")}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "none",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            background: filterNature === "" ? "#3B82F6" : "#F3F4F6",
            color: filterNature === "" ? "white" : "#374151",
            fontFamily: "DM Sans, sans-serif",
          }}
        >
          All ({accounts.length})
        </button>
        {ACCOUNT_NATURES.map(nature => (
          <button
            key={nature}
            onClick={() => setFilterNature(nature)}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "none",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              background: filterNature === nature ? "#3B82F6" : "#F3F4F6",
              color: filterNature === nature ? "white" : "#374151",
              fontFamily: "DM Sans, sans-serif",
            }}
          >
            {nature} ({accounts.filter(a => a.nature === nature).length})
          </button>
        ))}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: 16
        }}>
          <div style={{
            background: "white",
            borderRadius: 12,
            padding: 24,
            width: "100%",
            maxWidth: 450,
            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
          }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, fontFamily: "DM Sans, sans-serif" }}>
              {editingAccount ? "Edit Account" : "Add Account"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, fontFamily: "DM Sans, sans-serif" }}>
                  Account Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Office Supplies, Rent, Bank Account"
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, fontFamily: "DM Sans, sans-serif", outline: "none" }}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, fontFamily: "DM Sans, sans-serif" }}>
                  Nature *
                </label>
                <select
                  name="nature"
                  value={formData.nature}
                  onChange={handleChange}
                  required
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, fontFamily: "DM Sans, sans-serif", outline: "none", background: "white" }}
                >
                  {ACCOUNT_NATURES.map(nature => (
                    <option key={nature} value={nature}>{nature}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    background: saving ? "#9CA3AF" : "#3B82F6",
                    color: "white",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: "DM Sans, sans-serif",
                    cursor: saving ? "not-allowed" : "pointer",
                  }}
                >
                  {saving ? "Saving..." : (editingAccount ? "Update" : "Add Account")}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: "12px 24px",
                    background: "white",
                    color: "#6B7280",
                    border: "1px solid #D1D5DB",
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: "DM Sans, sans-serif",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Accounts by Nature */}
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {ACCOUNT_NATURES.map(nature => {
          const natureAccounts = groupedAccounts[nature];
          if (filterNature && filterNature !== nature) return null;
          if (natureAccounts.length === 0) return null;

          return (
            <div key={nature} className="account-table-container">
              <div style={{ 
                padding: "14px 20px", 
                background: (natureColors[nature] || "#3B82F6") + "15",
                borderBottom: "1px solid #E5E7EB",
                display: "flex",
                alignItems: "center",
                gap: 10
              }}>
                <div style={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: "50%", 
                  background: natureColors[nature] || "#3B82F6" 
                }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: "#1F2937", fontFamily: "DM Sans, sans-serif" }}>
                  {nature}
                </span>
                <span style={{ fontSize: 12, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>
                  ({natureAccounts.length} accounts)
                </span>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  {natureAccounts.map((account, index) => (
                    <tr key={account.id} style={{ borderBottom: index < natureAccounts.length - 1 ? "1px solid #E5E7EB" : "none" }}>
                      <td style={{ padding: "14px 20px", fontWeight: 500, color: "#1F2937", fontFamily: "DM Sans, sans-serif" }}>
                        {account.name}
                      </td>
                      <td style={{ padding: "14px 20px", textAlign: "right" }}>
                        <button
                          onClick={() => handleEdit(account)}
                          style={{ padding: "6px 12px", fontSize: 12, background: "#E5E7EB", color: "#374151", border: "none", borderRadius: 6, cursor: "pointer", marginRight: 8, fontFamily: "DM Sans, sans-serif" }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(account.id, account.name)}
                          style={{ padding: "6px 12px", fontSize: 12, background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="account-mobile-cards">
                {natureAccounts.map((account) => (
                  <div key={account.id} className="account-card">
                    <div style={{ fontWeight: 500, color: "#1F2937", fontSize: 14 }}>{account.name}</div>
                    <div className="account-actions">
                      <button
                        onClick={() => handleEdit(account)}
                        style={{ padding: "6px 10px", fontSize: 11, background: "#E5E7EB", color: "#374151", border: "none", borderRadius: 6, cursor: "pointer" }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(account.id, account.name)}
                        style={{ padding: "6px 10px", fontSize: 11, background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: 6, cursor: "pointer" }}
                      >
                        Del
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}